'use client';

import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import {
  selectIsGSAUser,
  selectIsGuestUser,
  selectIsLoggedIn,
  selectIsRecognizedUser,
  selectUserProfile,
} from '@next-app/lib/features/InitSelectors';
import SkeletonUI from '@next-app/components/Skeleton/Skeleton';
import {
  selectIsCheckoutDetailsLoaded,
  selectAllProfileAddresses,
  selectEmailInfo,
  selectShippingAddressInfo,
  selectIsShippingAddressCollapsed,
  selectEmailSectionCollapsed,
  selectIsShipOrder,
  selectDefaultProfileAddress,
  selectHasDefaultAddress,
  selectDigitalOnlyWebItemsInOrder,
  selectIsPureBopisOrder,
  selectIsPureDigitalOrder,
  selectHasItemFromRegistry,
  selectHasItemFromShippingList,
  selectIsPayPalPaymentMethodAuthorized,
  selectIsGooglePayAuthorized,
  selectPaymentDetailInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import { useEffect, useState } from 'react';
const ShowAddressModal = dynamic(
  () => import('@next-app/components/ShowAddressModal/ShowAddressModal'),
  { ssr: false },
);
import {
  ContactFormFields,
  ContactFormInitialState,
} from '../DeliveryAddressForm/DeliveryAddressFormFields';
import { IContactFormState } from '@next-app/interface/Checkout/ContactDeliverySection';
import LoggedInContactSection from '../DeliverySectionCard/LoggedInContactSection';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import { isEmptyObject } from '@next-app/components/shared/ProductDetails/helper';
import GSAUserRedirectModal from '@next-app/components/GSAUserRedirectModal/GSAUserRedirectModal';
import { getFromSessionStorage } from '@next-app/utils/session-storage-utils';
import {
  hideDynamicModal,
  showDynamicModal,
} from '@next-app/utils/common-utils';

const DeliverySectionCard = dynamic(
  () => import('../DeliverySectionCard/DeliverySectionCard'),
  {
    ssr: true,
    loading: () => <SkeletonUI width={'100%'} height={30} />,
  },
);

const DeliveryAddressForm = dynamic(
  () => import('../DeliveryAddressForm/DeliveryAddressForm'),
  {
    ssr: true,
    loading: () => <SkeletonUI width={'100%'} height={30} />,
  },
);

const ContactForm = dynamic(() => import('./ContactForm'), {
  ssr: true,
  loading: () => <SkeletonUI width={'100%'} height={30} />,
});

const DeliveryAddressList = dynamic(
  () => import('../DeliveryAddressList/DeliveryAddressList'),
  {
    ssr: true,
    loading: () => <SkeletonUI width={'100%'} height={30} />,
  },
);

const Contact = (): React.JSX.Element => {
  // selectors
  const isGuestUser = useSelector(selectIsGuestUser);
  const isRecognizedUser = useSelector(selectIsRecognizedUser);
  const defaultSelectedAddress = useSelector(selectDefaultProfileAddress) || {};
  const isCheckoutDetailsLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const addresses = useSelector(selectAllProfileAddresses);
  const { emailOptIn, email } = useSelector(selectEmailInfo) || {};
  const emailSectionCollapsed = useSelector(selectEmailSectionCollapsed);
  const isShipOrder = useSelector(selectIsShipOrder) || false;
  const isShippingAddressCollapsed = useSelector(
    selectIsShippingAddressCollapsed,
  );
  const hasDefaultAddress = useSelector(selectHasDefaultAddress) || false;
  const IsPureBopisOrder = useSelector(selectIsPureBopisOrder) || false;
  const IsPureDigitalOrder = useSelector(selectIsPureDigitalOrder) || false;

  const digitalOnlyWebItemsInOrder =
    useSelector(selectDigitalOnlyWebItemsInOrder) || false;
  const { email: sessionInfoEmail } = useSelector(selectUserProfile) || {};
  const isRegistryOrder = useSelector(selectHasItemFromRegistry);
  const isCTLOrder = useSelector(selectHasItemFromShippingList);
  const isPayPalAuthorized = useSelector(selectIsPayPalPaymentMethodAuthorized);
  const isGooglePayAuthorized = useSelector(selectIsGooglePayAuthorized);

  const isGSAUser = useSelector(selectIsGSAUser);

  const mainPaymentType = useSelector(selectPaymentDetailInfo)?.paymentsInfo
    ?.mainPaymentType;

  const initialCollapsedValue = isShipOrder
    ? isShippingAddressCollapsed
    : emailSectionCollapsed;
  const preContactState = {
    [ContactFormFields.EMAIL]: email || sessionInfoEmail || '',
    [ContactFormFields.PASSWORD]: '',
    [ContactFormFields.PROMO_CHECKBOX]: emailOptIn,
    [ContactFormFields.IS_GUEST_CHECKOUT]: isGuestUser,
  };

  // local state
  const [showAddressModal, setShowAddressModal] = useState(false);

  const shippingAddressInfo = useSelector(selectShippingAddressInfo) || '';
  const hintAddress = shippingAddressInfo?.hintAddress;

  const [contactFormState, setContactFormState] = useState<IContactFormState>(
    preContactState || ContactFormInitialState,
  );
  const [contactFormErrors, setContactFormErrors] = useState({
    [ContactFormFields.EMAIL]: '',
    [ContactFormFields.PASSWORD]: '',
  });

  const [showCollapse, setShowCollapse] = useState(!initialCollapsedValue);

  const [showGSAUserRedirectModal, setShowGSAUserRedirectModal] =
    useState(false);

  useEffect(() => {
    if (
      hintAddress != null &&
      hintAddress != '' &&
      (mainPaymentType === 'payPal' || mainPaymentType === 'googlePay')
    ) {
      setShowAddressModal(true);
      showDynamicModal();
    } else {
      setShowAddressModal(false);
      hideDynamicModal();
    }
  }, [hintAddress]);

  // useEffect to set promo checkbox state based on emailOptIn
  useEffect(() => {
    setContactFormState(preContactState);
    setShowCollapse(!initialCollapsedValue);
    if (isPayPalAuthorized) {
      setShowCollapse(false);
    }
    if (isGooglePayAuthorized) {
      setShowCollapse(!initialCollapsedValue);
    }
  }, [
    isCheckoutDetailsLoaded,
    isShippingAddressCollapsed,
    emailSectionCollapsed,
    isPayPalAuthorized,
    isGooglePayAuthorized,
  ]);

  useEffect(() => {
    setShowCollapse(!initialCollapsedValue);
  }, [!initialCollapsedValue, defaultSelectedAddress?.repositoryId]);

  useEffect(() => {
    if (isGSAUser && getFromSessionStorage('gsaUserRedirectModal') == 'true')
      setShowGSAUserRedirectModal(true);
  }, [isGSAUser]);

  return (
    <>
      <section className="mb-md-ll-5">
        <Conditional if={isCheckoutDetailsLoaded}>
          <>
            <Conditional if={!showCollapse}>
              <div className="my-md-ll-5">
                <DeliverySectionCard
                  setShowCollapse={setShowCollapse}
                  showCollapse={showCollapse}
                />
              </div>
            </Conditional>
            {/* expended contact section */}
            <Conditional if={showCollapse}>
              <>
                <div className="mt-ll-3 mb-ll-3">
                  {/* email info with logout link*/}
                  <Conditional if={isLoggedIn}>
                    <LoggedInContactSection />
                  </Conditional>
                  {/* contact form for guest user */}
                  <Conditional if={isGuestUser || isRecognizedUser}>
                    <ContactForm
                      setContactFormState={setContactFormState}
                      contactFormState={contactFormState}
                      setContactFormErrors={setContactFormErrors}
                      contactFormErrors={contactFormErrors}
                    />
                  </Conditional>
                </div>
                {/* address form for guest or new user for ship item */}
                <Conditional
                  if={
                    (!isLoggedIn && !isRegistryOrder && !isCTLOrder) ||
                    (isLoggedIn &&
                      addresses == null &&
                      isEmptyObject(defaultSelectedAddress) &&
                      isShipOrder) ||
                    (!isLoggedIn &&
                      (isRegistryOrder || isCTLOrder) &&
                      (IsPureBopisOrder || IsPureDigitalOrder))
                  }
                >
                  <div className="mt-ll-3 mt-lg-ll-5 mb-lg-ll-5">
                    <DeliveryAddressForm
                      formHeading="Delivery Address"
                      showContinue={!initialCollapsedValue}
                      id={'deliveryAddrNew'}
                      isNewAddress={!initialCollapsedValue}
                      address={
                        addresses !== null && !isRecognizedUser
                          ? defaultSelectedAddress
                          : null
                      }
                      showDefaultAddressSwitch={false}
                      contactFormState={contactFormState}
                      setShowCollapse={setShowCollapse}
                      contactFormErrors={contactFormErrors}
                      setShowAddressModal={setShowAddressModal}
                      setContactFormErrors={setContactFormErrors}
                      showAddressModal={showAddressModal}
                      onCancel={() => {
                        setShowCollapse(false);
                        setContactFormState(preContactState);
                      }}
                    />
                  </div>
                </Conditional>
                {/* address list for ship type order */}
                <Conditional
                  if={
                    hasDefaultAddress &&
                    isShipOrder &&
                    (isLoggedIn || isRegistryOrder || isCTLOrder)
                  }
                >
                  <DeliveryAddressList
                    contactFormErrors={contactFormErrors}
                    contactFormState={contactFormState}
                    setShowAddressModal={setShowAddressModal}
                    showAddressModal={showAddressModal}
                    setShowCollapse={setShowCollapse}
                    setContactFormErrors={setContactFormErrors}
                  />
                </Conditional>
                {/* only for loggedIn user without ship item */}
                <Conditional
                  if={
                    (IsPureDigitalOrder ||
                      IsPureBopisOrder ||
                      digitalOnlyWebItemsInOrder) &&
                    isLoggedIn
                  }
                >
                  <div className="d-md-flex justify-content-end">
                    <LLButton
                      type="submit"
                      variation="secondary"
                      customClass="col-12 col-md-auto"
                      onClick={() => setShowCollapse(false)}
                      automationId="co_shp_continue"
                    >
                      Continue
                    </LLButton>
                  </div>
                </Conditional>
              </>
            </Conditional>
          </>
        </Conditional>
        <Conditional if={!isCheckoutDetailsLoaded}>
          <SkeletonUI width={'100%'} height={30} />
        </Conditional>
        <Conditional if={showAddressModal}>
          <ShowAddressModal contactFormState={contactFormState} />
        </Conditional>
      </section>
      <Conditional if={showGSAUserRedirectModal}>
        <GSAUserRedirectModal />
      </Conditional>
    </>
  );
};
export default Contact;
